<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <b-modal
      v-model="modal.show"
      size="lg"
      :title="isEditMode ? `Edit Calculator` : `Create New Calculator`"
      :ok-disabled="disableSubmitButton"
      :ok-title="`Submit`"
      :hide-footer="loading"
      @ok.prevent="handleSubmit(onSubmit)"
      @hidden="onHideModal"
      @show="onShowModal"
    >
      <content-spinner :show="loading" />
      <div v-if="!loading">
        <b-form
          @submit.prevent="handleSubmit(onSubmit)"
          @keydown.enter.prevent="handleSubmit(onSubmit)"
        >
          <div class="row">
            <div class="col-sm-4">
              <input-widget
                :model="form"
                :field="fields.loanCalculatorName"
                :autofocus="fields.loanCalculatorName.autofocus"
                :disabled="isEditForbidden"
              />
            </div>
            <div class="col-sm-4">
              <input-widget
                :model="form"
                :field="fields.entityName"
                :autofocus="fields.entityName.autofocus"
                :disabled="fields.entityName.disabled"
              />
            </div>
            <div class="col-sm-4">
              <input-widget
                :model="form"
                :field="fields.postedDate"
                @change="onPostedDateChanged"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-sm-4">
              <input-widget
                :model="form"
                :field="fields.dayCountConvention"
                :autofocus="fields.dayCountConvention.autofocus"
                :disabled="isEditForbidden"
              />
            </div>
            <div class="col-sm-4">
              <input-widget
                :model="form"
                :field="fields.rollingFrequency"
                :autofocus="fields.rollingFrequency.autofocus"
                :disabled="isEditForbidden"
              />
            </div>
            <div class="col-sm-4">
              <input-widget
                :model="form"
                :field="fields.compoundingFrequency"
                :autofocus="fields.compoundingFrequency.autofocus"
                :disabled="isEditForbidden"
                :error="formErrors.compoundingFrequency"
              />
              <div v-if="formErrors.compoundingFrequency" class="invalid-feedback d-block">
                {{ formErrors.compoundingFrequency }}
              </div>
              <b-form-checkbox
                v-model="form.useLoanStartDate"
                class="mt-2"
                :value="true"
                :unchecked-value="false"
                :disabled="isEditForbidden"
              >
                Use Loan Start Date
              </b-form-checkbox>
            </div>
          </div>
          <div>
            <div v-for="(table, tableKey) in tableModels">
              <template v-if="isEditMode || !['loanMaturity', 'fundRates', 'rateValues'].includes(tableKey)">
                <div
                  class="header-toggle m-1 d-flex justify-content-between align-items-center"
                  @click="toggle(table)"
                >
                  <div class="table-title">
                    {{ table.name }}
                  </div>
                  <font-awesome-icon
                    v-if="table.collapse"
                    class="float-right"
                    :icon="['fas','chevron-down']"
                  />
                  <font-awesome-icon
                    v-else
                    class="float-right "
                    :icon="['fas','chevron-up']"
                  />
                </div>
                <b-collapse
                  :id="table.name"
                  :visible="!isEditMode"
                  class="px-4"
                >
                  <b-form-checkbox
                    v-if="!isEditMode && tableKey !== 'principalIncreaseTransaction'"
                    v-model="table.sameAs"
                    class="mt-3"
                    :value="true"
                    :unchecked-value="false"
                    :disabled="isEditForbidden"
                    @change="onTransactionSameCheckboxChange($event, tableKey, table)"
                  >
                    The same details as "Principal Increase"
                  </b-form-checkbox>
                  <div class="mt-3">
                    <input-widget
                      v-if="!table.sameAs || isEditMode"
                      :field="tableModelField"
                      :model="tableModels[tableKey].selected"
                      :disabled="isEditForbidden"
                      @change="onChangeTableModelField($event, tableKey)"
                    />
                    <template v-if="!!tableModelFields[tableKey].fields[0].data.options.length">
                      <input-widget
                        v-for="(field, idx) in tableModelFields[tableKey].fields"
                        v-if="!(table.sameAs && !isEditMode) || field.name === 'amountFieldId'"
                        :key="`${updateContent}-${idx}`"
                        :model="form[tableKey]"
                        :field="field"
                        :autofocus="field.autofocus"
                        :disabled="isEditForbidden"
                        @change="onChangeTableModelEntityField($event, tableKey)"
                      />
                    </template>
                    <b-form-checkbox
                      v-if="tableKey === 'principalRepaymentTransaction'"
                      v-model="form.calculateRepaymentEOD"
                      class="my-3"
                      :value="true"
                      :unchecked-value="false"
                      :disabled="isEditForbidden"
                    >
                      Calculate Repayment EOD
                    </b-form-checkbox>
                  </div>
                </b-collapse>
              </template>
            </div>
          </div>
          <input-widget
            :key="`decimal-points-${updateContent}`"
            :field="decimalPoints"
            :model="form"
            :disabled="isEditForbidden"
          />
          <b-form-checkbox
            v-model="showInvestorGroup"
            class="my-3"
            :value="true"
            :unchecked-value="false"
            :disabled="isEditForbidden"
          >
            Investor Group
          </b-form-checkbox>
          <div>
            <input-widget
              v-if="showInvestorGroupTables"
              :key="`investor-group-entity-${updateContent}`"
              :field="investorGroupEntity"
              :model="form"
              :disabled="isEditForbidden"
              @change="onChangeInvestorGroupEntity"
            />
            <input-widget
              v-if="showInvestorGroup"
              :key="`investor-group-field-${updateContent}`"
              :field="investorGroupField"
              :model="form"
              :disabled="isEditForbidden"
            />
          </div>
        </b-form>
      </div>
    </b-modal>
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../../core/components/ContentSpinner';
import InputWidget from '../../../core/components/complex-table/components/InputWidget';
import {v4 as uuidv4} from 'uuid';

const {
  mapState,
  mapActions,
} = createNamespacedHelpers('loanCalculator');

const {
  mapState: mapStateDataManagement,
  mapActions: mapActionsDataManagement,
} = createNamespacedHelpers('dataManagement');

const {
  mapState: sidebarMapState,
} = createNamespacedHelpers('sidebar');

export default {
  name: 'AddEditCalculatorModal',
  components: {
    InputWidget,
    ContentSpinner,
  },
  data() {
    return {
      disableSubmitButton: false,
      loading: false,
      showInvestorGroup: false,
      investorGroupEntity: {
        type: 'select',
        required: false,
        label: 'Table',
        name: 'investorGroupEntity',
        data: {
          options: [],
        },
      },
      investorGroupField: {
        type: 'select',
        required: true,
        label: 'Investor Group Field',
        name: 'investorGroup',
        data: {
          options: [],
        },
      },
      decimalPoints: {
        type: 'select',
        required: false,
        label: 'Decimal points rounding',
        name: 'decimalPoints',
        data: {
          options: [
            {value: 2, text: '2'},
            {value: 6, text: '6'},
          ],
        },
      },
      form: {
        loanCalculatorName: '',
        dayCountConvention: '',
        entityName: '',
        rollingFrequency: '',
        compoundingFrequency: '',
        useLoanStartDate: false,
        calculateRepaymentEOD: '',
        decimalPoints: '',
        investorGroupEntity: '',
        investorGroup: '',
        principalIncreaseTransaction: {
          transactionNameFieldId: '',
          entityFieldId: '',
          parentFieldId: '',
          instrumentFieldId: '',
          dateFieldId: '',
          amountFieldId: '',
        },
        principalRepaymentTransaction: {
          transactionNameFieldId: '',
          entityFieldId: '',
          parentFieldId: '',
          instrumentFieldId: '',
          dateFieldId: '',
          amountFieldId: '',
        },
        intrestRepaymentTransaction: {
          transactionNameFieldId: '',
          entityFieldId: '',
          parentFieldId: '',
          instrumentFieldId: '',
          dateFieldId: '',
          amountFieldId: '',
        },
        loanMaturity: {
          fundNameFieldId: '',
          issueDateFieldId: '',
          maturityDateFieldId: '',
        },
        rateValues: {
          indexRateFieldId: '',
          dateFieldId: '',
          rateFieldId: '',
        },
        fundRates: {
          fundNameFieldId: '',
          indexRateFieldId: '',
          offsetPercentageFieldId: '',
          startDateFieldId: '',
          endDateFieldId: '',
        },
      },
      formErrors: {
        compoundingFrequency: '',
      },
      tableModelField: {
        type: 'select',
        required: true,
        label: 'Table',
        name: 'value',
        data: {
          options: [],
        },
      },
      fields: {
        loanCalculatorName: {
          type: 'text',
          required: true,
          autofocus: true,
          label: 'Name',
          name: 'loanCalculatorName',
        },
        entityName: {
          type: 'text',
          disabled: true,
          label: 'Entity Name',
          name: 'entityName',
        },
        postedDate: {
          type: 'date',
          label: 'Posted Date',
          name: 'postedDate',
        },
        dayCountConvention: {
          type: 'select',
          required: true,
          label: 'Day Count Convention',
          name: 'dayCountConvention',
          data: {
            options: [
              {value: 'Actual_360', text: 'Actual_360'},
              {value: 'Actual_365', text: 'Actual_365'},
              {value: 'Actual_Actual', text: 'Actual_Actual'},
            ],
          },
        },
        rollingFrequency: {
          type: 'select',
          required: true,
          label: 'Rolling Frequency',
          name: 'rollingFrequency',
          data: {
            options: [
              {value: 'Daily', text: 'Daily'},
              {value: 'Monthly', text: 'Monthly'},
              {value: 'Quarterly', text: 'Quarterly'},
              {value: 'SemiAnnual', text: 'Semi Annual'},
              {value: 'Annual', text: 'Annual'},
            ],
          },
        },
        compoundingFrequency: {
          type: 'select',
          required: true,
          label: 'Compounding Frequency',
          name: 'compoundingFrequency',
          data: {
            options: [
              {value: 'None', text: 'None'},
              {value: 'Daily', text: 'Daily'},
              {value: 'Monthly', text: 'Monthly'},
              {value: 'Quarterly', text: 'Quarterly'},
              {value: 'SemiAnnual', text: 'Semi Annual'},
              {value: 'Annual', text: 'Annual'},
            ],
          },
        },
      },
      tableModelFields: {
        principalIncreaseTransaction: {
          fields: [
            {
              type: 'select',
              required: true,
              label: 'Transaction Name',
              name: 'transactionNameFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Entity',
              name: 'entityFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: false,
              label: 'Parent Field',
              name: 'parentFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: false,
              label: 'Instrument Field',
              name: 'instrumentFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Date',
              name: 'dateFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Amount',
              name: 'amountFieldId',
              data: {
                options: [],
              },
            },
          ],
        },
        principalRepaymentTransaction: {
          fields: [
            {
              type: 'select',
              required: true,
              label: 'Transaction Name',
              name: 'transactionNameFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Entity',
              name: 'entityFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: false,
              label: 'Parent Field',
              name: 'parentFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: false,
              label: 'Instrument Field',
              name: 'instrumentFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Date',
              name: 'dateFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Amount',
              name: 'amountFieldId',
              data: {
                options: [],
              },
            },
          ],
        },
        intrestRepaymentTransaction: {
          fields: [
            {
              type: 'select',
              required: true,
              label: 'Transaction Name',
              name: 'transactionNameFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Entity',
              name: 'entityFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: false,
              label: 'Parent Field',
              name: 'parentFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: false,
              label: 'Instrument Field',
              name: 'instrumentFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Date',
              name: 'dateFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Amount',
              name: 'amountFieldId',
              data: {
                options: [],
              },
            },
          ],
        },
        loanMaturity: {
          fields: [
            {
              type: 'select',
              required: true,
              label: 'Fund Name',
              name: 'fundNameFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Issue Date',
              name: 'issueDateFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Maturity Date',
              name: 'maturityDateFieldId',
              data: {
                options: [],
              },
            },
          ],
        },
        rateValues: {
          fields: [
            {
              type: 'select',
              required: true,
              label: 'Index Rate',
              name: 'indexRateFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Date',
              name: 'dateFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Rate',
              name: 'rateFieldId',
              data: {
                options: [],
              },
            },
          ],
        },
        fundRates: {
          fields: [
            {
              type: 'select',
              required: true,
              label: 'Fund Name',
              name: 'fundNameFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Index Rate',
              name: 'indexRateFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Offset Percentage',
              name: 'offsetPercentageFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'Start Date',
              name: 'startDateFieldId',
              data: {
                options: [],
              },
            },
            {
              type: 'select',
              required: true,
              label: 'End Date',
              name: 'endDateFieldId',
              data: {
                options: [],
              },
            },
          ],
        },
      },
      tableModels: {
        principalIncreaseTransaction: {
          name: 'Principal Increase',
          selected: {
            value: '',
          },
        },
        principalRepaymentTransaction: {
          name: 'Principal Repayment',
          selected: {
            value: '',
          },
        },
        intrestRepaymentTransaction: {
          name: 'Interest Repayment',
          selected: {
            value: '',
          },
        },
        loanMaturity: {
          name: 'Loan Maturity',
          selected: {
            value: '',
          },
        },
        fundRates: {
          name: 'Fund Rates',
          selected: {
            value: '',
          },
        },
        rateValues: {
          name: 'Rate Values',
          selected: {
            value: '',
          },
        },
      },
      updateContent: 0,
      showInvestorGroupTables: false,
    };
  },
  watch: {
    'form.principalIncreaseTransaction.entityFieldId': {
      handler() {
        this.populateInvestorGroupFieldDropdown();
      },
      immediate: true,
    },
    showInvestorGroup: {
      handler() {
        this.populateInvestorGroupFieldDropdown();
      },
    },
  },
  computed: {
    ...mapStateDataManagement({
      tables: state => state.tables.data,
      allTableFields: state => state.fields.data,
      allTableFieldsLoaded: state => state.fields.loaded,
    }),
    ...mapState({
      modal: state => state.addEditModal,
      modalData: state => state.addEditModal.data,
    }),
    ...sidebarMapState({
      tablesList: state => state.tablesList,
    }),
    isEditMode() {
      return !!Object.keys(this.modalData).length;
    },
    isEditForbidden() {
      return !!this.modal.data.postedDate;
    },
    frequencyRank() {
      return {
        'Daily': 1,
        'Monthly': 2,
        'Quarterly': 3,
        'SemiAnnual': 4,
        'Annual': 5,
        'None': 6,
      };
    },
  },
  methods: {
    ...mapActions([
      'upsertCalculator',
      'hideAddEditModal',
    ]),
    ...mapActionsDataManagement([
      'getFields',
    ]),
    populateInvestorGroupFieldDropdown() {
      this.showInvestorGroupTables = false;
      this.investorGroupField.data.options = [];
      if (!this.showInvestorGroup) return;

      const uuid = this.form.principalIncreaseTransaction && this.form.principalIncreaseTransaction.entityFieldId;
      if (!uuid) {
        this.showInvestorGroupTables = true;
        return;
      }

      const field = this.allTableFields.find(f => f.fieldGuid === uuid);

      if (field && field.dataTypeName === 'lookup') {
        const config = JSON.parse(field.dataTypeJSON);
        if (config.FieldToDisplayGuid) {
          const relatedField = this.allTableFields.find(f => f.fieldGuid === config.FieldToDisplayGuid);
          const allFieldsOfRelatedEntity = this.allTableFields.filter(f => f.entityGuid === relatedField.entityGuid);

          this.investorGroupField.data.options = allFieldsOfRelatedEntity
            .map((field) => ({value: field.fieldGuid, text: field.displayName}))
            .sort((a, b) => a.text.localeCompare(b.text))
          ;

        }
      } else {
        this.showInvestorGroupTables = true;
      }
      this.investorGroupField = {...this.investorGroupField};
    },
    validateCompoundingFrequency() {
      const rolling = this.form.rollingFrequency;
      const compounding = this.form.compoundingFrequency;
      this.formErrors.compoundingFrequency = '';

      if (rolling && compounding && this.frequencyRank[compounding] < this.frequencyRank[rolling]) {
        this.formErrors.compoundingFrequency = 'Compounding frequency cannot be more frequent than rolling frequency.';
        return false;
      }
      return true;
    },
    async onSubmit() {
      this.loading = this.disableSubmitButton = true;

      if (!this.validateCompoundingFrequency()) {
        this.loading = this.disableSubmitButton = false;
        return;
      }

      const postedDate = this.form.postedDate;
      if (postedDate) {
        this.form.postedDate = this.convertTime(this.form.postedDate);
      }

      const params = JSON.parse(JSON.stringify(this.form));
      if (!this.isEditMode) {
        delete params.investorGroupEntity;
        delete params.loanMaturity;
        delete params.rateValues;
        delete params.fundRates;
      }

      if (!this.showInvestorGroup) {
        params.investorGroup = null;
      }

      params.loanCalculatorId = this.isEditMode ? this.modalData.loanCalculatorId : uuidv4();
      const action = this.isEditMode ? 'update' : 'create';

      const {success, body} = await this.upsertCalculator({params, action});
      if (success) {
        this.$toasted.success(`Loan Calculator ${action}d successfully`, {
          duration: TOASTER_DURATION,
        });
        this.$emit('update', body);
        this.hideModal();
      } else {
        let error = body.ErrorMessage;
        if (!error && Object.keys(body.errors).length) {
          error = body.errors[Object.keys(body.errors)[0]];
          if (_.isArray(error)) {
            error = error.join('\n');
          }
        }
        this.$toasted.error(error, {
          duration: TOASTER_DURATION,
        });
      }

      this.loading = this.disableSubmitButton = false;
    },
    hideModal() {
      this.hideAddEditModal();
      this.onHideModal();
    },
    onHideModal() {
      Object.keys(this.form).forEach(key => {
        if (this.form[key] && typeof this.form[key] === 'object') {
          Object.keys(this.form[key]).forEach(k => {
            this.form[key][k] = '';
          });
        } else {
          this.form[key] = '';
        }

        if (this.tableModelFields[key] && Array.isArray(this.tableModelFields[key].fields)) {
          this.tableModelFields[key].fields.forEach(f => {
            f.data.options = [];
          });
          this.tableModels[key].selected.value = '';
        }
      });

      this.showInvestorGroup = false;
      this.investorGroupEntity.data.options = [];
      this.investorGroupField.data.options = [];
    },
    async onShowModal() {
      if (!this.allTableFieldsLoaded) {
        await this.getFields();
        this.$nextTick(() => {
          this.prepareData();
        });
      } else {
        this.prepareData();
      }

    },
    onChangeTableModelField(entityGuid, tableModelName) {
      const optionsFromTableFields = this.allTableFields
        .reduce((options, field) => {
          if (field.entityGuid === entityGuid && (!field.isInternal && !field.isPrimary)) {
            options.push({
              value: field.fieldGuid,
              text: field.displayName,
            });
          }
          return options;
        }, [])
        .sort((a, b) => a.text.localeCompare(b.text));

      this.tableModelFields[tableModelName].fields.forEach(f => {
        f.data.options = optionsFromTableFields;
      });

      this.updateContent += 1;
    },
    onChangeTableModelEntityField(uuid, tableKey) {

    },
    prepareTableModelFieldOptions() {
      this.tableModelField.data.options = Object.values(this.tablesList)
        .map(t => ({value: t.entityGuid, text: t.label}))
        .sort((a, b) => a.text.localeCompare(b.text));

      this.investorGroupEntity.data.options = Object.values(this.tablesList)
        .map(t => ({value: t.entityGuid, text: t.label}))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    prepareFormData() {
      this.form = JSON.parse(JSON.stringify(this.modalData));
      if (this.form.investorGroup) {
        this.showInvestorGroup = true;
      }

      this.form.entityName = 'Entity not created';

      if (this.form.entityGuid) {
        const foundEntity = Object.values(this.tablesList).find(t => t.entityGuid === this.form.entityGuid);
        if (foundEntity && foundEntity.displayName) {
          this.form.entityName = foundEntity.displayName;
        }
      }

      Object.keys(this.form).forEach((key) => {
        if (typeof this.form[key] === 'object') {
          let fieldGuid;
          if (this.form[key]) {
            Object.keys(this.form[key]).forEach(k => {
              const attribute = this.form[key][k];
              if (attribute) {
                fieldGuid = attribute;
                return true;
              }
            });
            //console.log(`key: ${key}, fieldGuid: ${fieldGuid}`);
            const entityGuid = this.allTableFields.find(f => f.fieldGuid === fieldGuid).entityGuid;
            //console.log(`entityGuid: ${entityGuid}`);
            this.tableModels[key].selected.value = entityGuid;
            this.onChangeTableModelField(entityGuid, key);
          }
        }
      });

      if (this.form.postedDate) {
        this.form.postedDate = new Date(this.form.postedDate);
      }
    },
    prepareData() {
      this.loading = true;
      setTimeout(() => {
        this.prepareTableModelFieldOptions();
        this.form.decimalPoints = 2;

        if (this.isEditMode) {
          this.prepareFormData();
        }
        this.loading = false;
      }, 500);
    },
    toggle(table) {
      table.collapse = !table.collapse;
      this.$root.$emit('bv::toggle::collapse', table.name);
    },
    onTransactionSameCheckboxChange(value, tableKey, table) {
      if (value) {
        this.tableModels[tableKey].selected = this.tableModels.principalIncreaseTransaction.selected;
        this.tableModelFields[tableKey] = {...this.tableModelFields.principalIncreaseTransaction};
        this.form[tableKey] = {...this.form.principalIncreaseTransaction, amountFieldId: ''};
      }
    },
    onChangeInvestorGroupEntity() {
      const entityGuid = this.form.investorGroupEntity;
      this.investorGroupField.data.options = this.allTableFields
        .reduce((options, field) => {
          if (field.entityGuid === entityGuid && (!field.isInternal && !field.isPrimary)) {
            options.push({
              value: field.fieldGuid,
              text: field.displayName,
            });
          }
          return options;
        }, [])
        .sort((a, b) => a.text.localeCompare(b.text));


      this.updateContent += 1;
    },
    convertTime(date) {
      const year = date.toLocaleString('default', {year: 'numeric'});
      const month = date.toLocaleString('default', {
        month: '2-digit',
      });
      const day = date.toLocaleString('default', {day: '2-digit'});

      return [year, month, day].join('-');
    },
    async onPostedDateChanged(ev) {
      // On posted date remove
      if (!!this.modal.data.postedDate && ev === null) {
        const success = await this.$confirm('Confirmed Posted Dated Deleting', `Removing the 'Posted Date' from the
        Loan Calculator allows the system to recalculate the entire resulting table each time Calculate is triggered
        (manually or through workflow). If any historic transaction is changed (added/removed), this will be reflected
        in calculation result.`);

        if (!success) {
          this.form.postedDate = new Date(this.modal.data.postedDate);
        }
      }
      // On posted date set
      if (!this.modal.data.postedDate && ev !== null) {
        const success = await this.$confirm('Confirmed Posted Dated Setting', `Setting a 'Posted Date' on the Loan
        Calculator freezes all calculations up to that date. You won't be able to edit the configuration or manually
        delete the calculator afterward - until you remove 'Posted Date'. This ensures the locked rows remain
        unchanged.`);

        if (!success) {
          this.form.postedDate = null;
        }
      }
    },
  },
};
</script>

<style>
.table-field label {
  border-bottom: 1px dashed #0F355E;
}

.table-title {
  font-weight: bold;
  font-size: 15px;
}
</style>
